import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useRef } from 'react';
import { includes as _includes } from 'lodash';
import PropTypes from 'prop-types';
import { utils } from '@gisatcz/ptr-utils';
import { GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import helpers from '../helpers';
import memoize from 'moize';
import shapes from '../shapes';
import MarkerShape from '../MarkerShape';
import { jsx as _jsx } from "react/jsx-runtime";
var GeoJsonLayer = function GeoJsonLayer(_ref) {
  var omittedFeatureKeys = _ref.omittedFeatureKeys,
    features = _ref.features,
    styleDefinition = _ref.styleDefinition,
    hoveredStyleDefinition = _ref.hoveredStyleDefinition,
    onFeatureClick = _ref.onFeatureClick,
    selectable = _ref.selectable,
    hoverable = _ref.hoverable,
    pointAsMarker = _ref.pointAsMarker,
    paneName = _ref.paneName,
    icons = _ref.icons,
    fidColumnName = _ref.fidColumnName;
  var getRenderId = useRef(memoize(function (features) {
    if (features) {
      return utils.uuid();
    }
  }));
  var getStyle = function getStyle(feature) {
    var styles = helpers.calculateStyle(feature.feature, styleDefinition, hoveredStyleDefinition, feature.selected, feature.selectedStyleDefinition, feature.selectedHoveredStyleDefinition);
    if (feature.selected) {
      return styles.selected;
    } else {
      return styles["default"];
    }
  };

  /**
   * @param id {string}
   * @param style {Object} Leaflet style definition
   * @param options {Object}
   * @param options.icons {Object} see https://gisat.github.io/components/maps/map#resources
   * @param options.onMouseMove {function}
   * @param options.onMouseOver {function}
   * @param options.onMouseOut {function}
   * @param options.onClick {function}
   * @return {MarkerShape}
   */
  var getMarkerShape = function getMarkerShape(id, style, options) {
    var shapeKey = style.shape;
    var iconKey = style.icon;
    var basicShape = true;
    var anchorShift = 0; // shift of anchor in pixels
    var anchorPositionX = 0.5; // relative anchor X position (0.5 means that the shape reference point is in the middle horizontally)
    var anchorPositionY = 0.5; // relative anchor Y position
    var shape, icon;

    // find shape by key in the internal set of shapes
    if (shapeKey) {
      shape = shapes[shapeKey] || null;
    }

    // find icon by key in the given set of icons
    if (iconKey) {
      var _options$icons;
      icon = (options === null || options === void 0 ? void 0 : (_options$icons = options.icons) === null || _options$icons === void 0 ? void 0 : _options$icons[iconKey]) || null;
    }
    if (shape || icon) {
      var _shape, _icon;
      basicShape = false;

      // get anchor positions from definitions, if exist
      if ((_shape = shape) !== null && _shape !== void 0 && _shape.anchorPoint) {
        anchorPositionX = shape.anchorPoint[0];
        anchorPositionY = shape.anchorPoint[1];
      } else if (!shape && (_icon = icon) !== null && _icon !== void 0 && _icon.anchorPoint) {
        anchorPositionX = icon.anchorPoint[0];
        anchorPositionY = icon.anchorPoint[1];
      }

      // if outline, shift the anchor point
      if (style !== null && style !== void 0 && style.weight) {
        anchorShift = style === null || style === void 0 ? void 0 : style.weight;
      }
    }
    return new MarkerShape({
      basicShape: basicShape,
      id: id,
      style: style,
      iconAnchor: style.radius ? [(2 * style.radius + anchorShift) * anchorPositionX, (2 * style.radius + anchorShift) * anchorPositionY] : null,
      icon: icon,
      shape: shape,
      onMouseMove: options.onMouseMove,
      onMouseOut: options.onMouseOut,
      onMouseOver: options.onMouseOver,
      onClick: options.onClick
    });
  };
  var onEachFeature = function onEachFeature(feature, layer) {
    var geometryType = feature.geometry.type;
    var isPolygon = geometryType === 'Polygon' || geometryType === 'MultiPolygon';
    var isLine = geometryType === 'Line' || geometryType === 'LineString';
    var styles = helpers.calculateStyle(feature.feature, styleDefinition, hoveredStyleDefinition, feature.selected, feature.selectedStyleDefinition, feature.selectedHoveredStyleDefinition);
    layer.on({
      click: function click() {
        if (onFeatureClick && selectable && feature.fid) {
          onFeatureClick(feature.fid);
        }
      },
      mouseover: function mouseover(e) {
        if (!selectable && !hoverable) {
          e.originalEvent.target.className.baseVal = '';
        }
      },
      mousemove: function mousemove(e) {
        if (hoverable) {
          if (feature.selected && styles.selectedHovered) {
            setStyle(styles.selectedHovered, e.target);
          } else {
            setStyle(styles.hovered, e.target);
          }
          if (isPolygon || isLine) {
            layer.bringToFront();
          }
        }
      },
      mouseout: function mouseout(e) {
        if (hoverable) {
          if (feature.selected && styles.selected) {
            setStyle(styles.selected, e.target);
          } else {
            setStyle(styles["default"], e.target);
          }
          if ((isLine || isPolygon) && !feature.selected) {
            layer.bringToBack();
          }
        }
      }
    });
  };

  /**
   * Set style of the feature
   * @param leafletStyle {Object} Leaflet style definition
   * @param element {Object} Leaflet element
   */
  var setStyle = function setStyle(leafletStyle, element) {
    var _element$options;
    var shape = element === null || element === void 0 ? void 0 : (_element$options = element.options) === null || _element$options === void 0 ? void 0 : _element$options.icon;
    if (shape) {
      shape.setStyle(leafletStyle, shape.id, shape.isBasicShape);
    } else {
      element.setStyle(leafletStyle);
    }
  };

  // render points
  var pointToLayer = function pointToLayer(feature, coord) {
    if (pointAsMarker) {
      var _style, _style2, _style3;
      var style = feature.defaultStyle;

      // for circles, use L.circleMarker due to better performance
      if (!((_style = style) !== null && _style !== void 0 && _style.shape) && !((_style2 = style) !== null && _style2 !== void 0 && _style2.icon) || ((_style3 = style) === null || _style3 === void 0 ? void 0 : _style3.shape) === 'circle') {
        return L.circleMarker(coord, _objectSpread(_objectSpread({}, feature.defaultStyle), {}, {
          pane: paneName
        }));
      } else {
        if (feature.selected) {
          var styles = helpers.calculateStyle(feature.feature, styleDefinition, hoveredStyleDefinition, feature.selected, feature.selectedStyleDefinition, feature.selectedHoveredStyleDefinition);
          style = styles.selected;
        }
        var shapeId = feature.uniqueFeatureKey ? "".concat(feature.uniqueFeatureKey, "_icon") : utils.uuid();
        var shape = getMarkerShape(shapeId, style, {
          icons: icons,
          onClick: function onClick(e) {
            selectable && onFeatureClick(feature.fid, {
              x: e.x,
              y: e.y
            });
          }
          // TODO on events
        });

        return L.marker(coord, {
          pane: paneName,
          interactive: hoverable || selectable,
          icon: shape
        });
      }
    } else {
      return L.circle(coord, feature.defaultStyle);
    }
  };
  var filter = function filter(feature) {
    if (omittedFeatureKeys) {
      var featureKey = feature.id || feature.properties[fidColumnName];
      return !(featureKey && _includes(omittedFeatureKeys, featureKey));
    } else {
      return true;
    }
  };
  var meggedFeatures = features.map(function (item) {
    return _objectSpread(_objectSpread({}, item.feature), item);
  });

  // generate new key on features change to return the new instance
  // more: https://react-leaflet.js.org/docs/en/components#geojson
  var key = getRenderId.current(meggedFeatures);
  return /*#__PURE__*/_jsx(GeoJSON, {
    data: meggedFeatures,
    style: getStyle,
    onEachFeature: onEachFeature,
    pointToLayer: pointToLayer,
    filter: filter
  }, key);
};
GeoJsonLayer.propTypes = {
  omittedFeatureKeys: PropTypes.array,
  // list of feature keys that shouldn't be rendered
  features: PropTypes.array,
  styleDefinition: PropTypes.object,
  hoveredStyleDefinition: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onFeatureClick: PropTypes.func,
  selectable: PropTypes.bool,
  hoverable: PropTypes.bool,
  pointAsMarker: PropTypes.bool,
  paneName: PropTypes.string,
  icons: PropTypes.object,
  fidColumnName: PropTypes.string
};
export default GeoJsonLayer;